import React, { useState, useEffect, createRef } from "react";
import { useNavigate } from "react-router-dom";
import CustomizableTable from "@/components/CustomizableTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, PlusCircle, Edit, Trash, ImagePlus, Loader2, X, Download, Upload } from "lucide-react";
import { 
  Product, 
  SupabaseProduct, 
  mapSupabaseProduct,
  Terpene,
  TerpeneWithPercentage,
  TerpeneInfo
} from "@/types";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { v4 as uuidv4 } from "uuid";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { exportProductsToCSV, importProductsFromCSV, checkForDuplicateCultivars } from "@/utils/csvUtils";

import CannabinoidPercentages from "@/components/product/CannabinoidPercentages";
import ProductRatings from "@/components/product/ProductRatings";
import TerpenePercentages from "@/components/product/TerpenePercentages";

type ProductFormat = 'flower' | 'hash' | 'live rosin' | 'resin';
type GrowStyle = 'indoor' | 'outdoor' | 'greenhouse';
type GrowMedia = 'living soil' | 'coco' | 'rockwool' | 'other';
type ProductOption = 'Bulk API' | 'Finished goods';
type GrowMethod = 'indoor' | 'outdoor' | 'glasshouse';
type FeedType = 'salts' | 'organic';

const TERPENE_OPTIONS: Terpene[] = [
  'Myrcene',
  'Limonene',
  'Caryophyllene',
  'Terpinolene',
  'Pinene',
  'Humulene',
  'Linalool',
  'Ocimene',
  'Terpineol',
  'Valencene',
  'Geraniol',
  'Bisabolol'
];

interface Column {
  key: string;
  header: string;
  render?: (value: any, row: any) => React.ReactNode;
}

const ProductsAdmin = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isNewProductDialogOpen, setIsNewProductDialogOpen] = useState(false);
  const [newProductCultivar, setNewProductCultivar] = useState("");
  const [newProductDescription, setNewProductDescription] = useState("");
  const [newProductImages, setNewProductImages] = useState<string[]>([]);
  const [newProductIndicaPercentage, setNewProductIndicaPercentage] = useState<number | undefined>(undefined);
  const [newProductThcPercentage, setNewProductThcPercentage] = useState<number | undefined>(undefined);
  const [newProductCbdPercentage, setNewProductCbdPercentage] = useState<number | undefined>(undefined);
  const [newProductCbgPercentage, setNewProductCbgPercentage] = useState<number | undefined>(undefined);
  const [newProductCbnPercentage, setNewProductCbnPercentage] = useState<number | undefined>(undefined);
  const [newProductCbcPercentage, setNewProductCbcPercentage] = useState<number | undefined>(undefined);
  const [newProductThcvPercentage, setNewProductThcvPercentage] = useState<number | undefined>(undefined);
  const [newProductPrimaryTerpenes, setNewProductPrimaryTerpenes] = useState<TerpeneWithPercentage[]>([]);
  const [newProductParents, setNewProductParents] = useState<string[]>([]);
  const [newProductAroma, setNewProductAroma] = useState("");
  const [newProductFeeling, setNewProductFeeling] = useState("");
  const [newProductAdvertisingStartDate, setNewProductAdvertisingStartDate] = useState<string | undefined>(undefined);
  const [newProductAdvertisingEndDate, setNewProductAdvertisingEndDate] = useState<string | undefined>(undefined);
  const [newProductRatingDank, setNewProductRatingDank] = useState<number | undefined>(undefined);
  const [newProductRatingEarthy, setNewProductRatingEarthy] = useState<number | undefined>(undefined);
  const [newProductRatingPungent, setNewProductRatingPungent] = useState<number | undefined>(undefined);
  const [newProductRatingDessert, setNewProductRatingDessert] = useState<number | undefined>(undefined);
  const [newProductRatingFruity, setNewProductRatingFruity] = useState<number | undefined>(undefined);
  const [newProductRatingCitrus, setNewProductRatingCitrus] = useState<number | undefined>(undefined);
  const [newProductFormat, setNewProductFormat] = useState<ProductFormat | undefined>(undefined);
  const [newProductAvailableDate, setNewProductAvailableDate] = useState<string | undefined>(undefined);
  const [newProductGrowStyle, setNewProductGrowStyle] = useState<GrowStyle | undefined>(undefined);
  const [newProductGrowMedia, setNewProductGrowMedia] = useState<GrowMedia | undefined>(undefined);
  const [newProductCosts, setNewProductCosts] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [newImage, setNewImage] = useState<File | null>(null);
  const [newImageUrl, setNewImageUrl] = useState<string | null>(null);
  const [newProduct, setNewProduct] = useState<Product | null>(null);
  const [newTerpeneName, setNewTerpeneName] = useState<Terpene>('Myrcene');
  const [newTerpenePercentage, setNewTerpenePercentage] = useState<number>(0);
  const [newTerpeneOptions, setNewTerpeneOptions] = useState<Terpene[]>(TERPENE_OPTIONS);
  const [newProductAdvertisingStartDateString, setNewProductAdvertisingStartDateString] = useState<string>("");
  const [newProductAdvertisingEndDateString, setNewProductAdvertisingEndDateString] = useState<string>("");
  const [newProductAvailableDateString, setNewProductAvailableDateString] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [newProductName, setNewProductName] = useState("");
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);
  const [duplicateCultivars, setDuplicateCultivars] = useState<string[]>([]);
  const [pendingProducts, setPendingProducts] = useState<Product[]>([]);
  const [importMode, setImportMode] = useState<'skip' | 'update'>('skip');

  const navigate = useNavigate();
  const { toast } = useToast();
  const inputRef = createRef<HTMLInputElement>();

  useEffect(() => {
    getFilteredProducts();
    fetchProducts();
  }, [searchQuery]);

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*');

      if (error) {
        console.error("Error fetching products:", error);
        toast({
          title: "Error",
          description: "Failed to fetch products from Supabase.",
          variant: "destructive",
        });
        return;
      }

      const mappedProducts = data.map(mapSupabaseProduct);
      setProducts(mappedProducts);
      setFilteredProducts(mappedProducts);
    } catch (error) {
      console.error("Error fetching products:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred while fetching products.",
        variant: "destructive",
      });
    }
  };

  const getFilteredProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .ilike('cultivar', `%${searchQuery}%`);

      if (error) {
        console.error("Error fetching products:", error);
        toast({
          title: "Error",
          description: "Failed to fetch products from Supabase.",
          variant: "destructive",
        });
        return;
      }

      const mappedProducts = data.map(mapSupabaseProduct);
      setFilteredProducts(mappedProducts);
    } catch (error) {
      console.error("Error fetching products:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred while fetching products.",
        variant: "destructive",
      });
    }
  };

  const handleProductClick = (product: Product) => {
    setSelectedProduct(product);
    setIsDialogOpen(true);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedImage(file);
    }
  };

  const handleAddImage = async () => {
    if (!selectedProduct || !selectedImage) {
      toast({
        title: "Error",
        description: "Please select a product and an image to upload.",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);
    try {
      const fileExt = selectedImage.name.split('.').pop();
      const fileName = `${uuidv4()}.${fileExt}`;
      const filePath = `products/${selectedProduct.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, selectedImage, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error("Error uploading image:", uploadError);
        toast({
          title: "Error",
          description: "Failed to upload image to Supabase storage.",
          variant: "destructive",
        });
        setUploading(false);
        return;
      }

      const publicUrl = `https://ensrbmzupjeiszivcwyv.supabase.co/storage/v1/object/public/images/${filePath}`;

      const { data: productData, error: updateError } = await supabase
        .from('products')
        .update({ images: [...selectedProduct.images, publicUrl] })
        .eq('id', selectedProduct.id)
        .select()

      if (updateError) {
        console.error("Error updating product images:", updateError);
        toast({
          title: "Error",
          description: "Failed to update product images in Supabase.",
          variant: "destructive",
        });
        setUploading(false);
        return;
      }

      const updatedProduct = mapSupabaseProduct(productData[0]);
      setSelectedProduct(updatedProduct);
      setProducts(products.map(p => p.id === selectedProduct.id ? updatedProduct : p));
      setFilteredProducts(filteredProducts.map(p => p.id === selectedProduct.id ? updatedProduct : p));
    } catch (error) {
      console.error("Error uploading image:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred while uploading the image.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = async (imageUrl: string) => {
    if (!selectedProduct) {
      toast({
        title: "Error",
        description: "No product selected.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      const updatedImages = selectedProduct.images.filter(img => img !== imageUrl);

      const { data: productData, error: updateError } = await supabase
        .from('products')
        .update({ images: updatedImages })
        .eq('id', selectedProduct.id)
        .select()

      if (updateError) {
        console.error("Error updating product images:", updateError);
        toast({
          title: "Error",
          description: "Failed to update product images in Supabase.",
          variant: "destructive",
        });
        setIsSaving(false);
        return;
      }

      const updatedProduct = mapSupabaseProduct(productData[0]);
      setSelectedProduct(updatedProduct);
      setProducts(products.map(p => p.id === selectedProduct.id ? updatedProduct : p));
      setFilteredProducts(filteredProducts.map(p => p.id === selectedProduct.id ? updatedProduct : p));

      toast({
        title: "Success",
        description: "Image removed and product updated successfully.",
      });
    } catch (error) {
      console.error("Error removing image:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred while removing the image.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteProduct = async (productId: string) => {
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', productId);

      if (error) {
        console.error("Error deleting product:", error);
        toast({
          title: "Error",
          description: "Failed to delete product from Supabase.",
          variant: "destructive",
        });
        setIsSaving(false);
        return;
      }

      setProducts(products.filter(p => p.id !== productId));
      setFilteredProducts(filteredProducts.filter(p => p.id !== productId));
      setIsDialogOpen(false);

      toast({
        title: "Success",
        description: "Product deleted successfully.",
      });
    } catch (error) {
      console.error("Error deleting product:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred while deleting the product.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveProduct = async () => {
    if (!selectedProduct) {
      toast({
        title: "Error",
        description: "No product selected.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      const advertisingStartDate = selectedProduct.advertising_start_date?.trim() === "" 
        ? null 
        : selectedProduct.advertising_start_date;
      
      const advertisingEndDate = selectedProduct.advertising_end_date?.trim() === "" 
        ? null 
        : selectedProduct.advertising_end_date;

      // Map terpene_info back to the primary_terpenes for Supabase
      const primaryTerpenes = selectedProduct.terpene_info.major_terpenes.map(t => ({
        name: t.name,
        percentage: t.percentage
      }));

      const { error } = await supabase
        .from('products')
        .update({
          cultivar: selectedProduct.cultivar,
          description: selectedProduct.description,
          indica_percentage: selectedProduct.indica_percentage,
          parents: selectedProduct.parents,
          aroma: selectedProduct.aroma,
          feeling: selectedProduct.feeling,
          advertising_start_date: advertisingStartDate,
          advertising_end_date: advertisingEndDate,
          rating_dank: selectedProduct.rating_dank,
          rating_earthy: selectedProduct.rating_earthy,
          rating_pungent: selectedProduct.rating_pungent,
          rating_dessert: selectedProduct.rating_dessert,
          rating_fruity: selectedProduct.rating_fruity,
          rating_citrus: selectedProduct.rating_citrus,
          format: selectedProduct.format,
          available_date: selectedProduct.availableDate?.trim() === "" ? null : selectedProduct.availableDate,
          grow_style: selectedProduct.growStyle,
          grow_media: selectedProduct.growMedia
        })
        .eq('id', selectedProduct.id);

      if (error) {
        console.error("Error updating product:", error);
        toast({
          title: "Error",
          description: "Failed to update product in Supabase.",
          variant: "destructive",
        });
        setIsSaving(false);
        return;
      }

      setProducts(products.map(p => p.id === selectedProduct.id ? selectedProduct : p));
      setFilteredProducts(filteredProducts.map(p => p.id === selectedProduct.id ? selectedProduct : p));
      setIsDialogOpen(false);

      toast({
        title: "Success",
        description: "Product updated successfully.",
      });
    } catch (error) {
      console.error("Error updating product:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred while updating the product.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateProduct = () => {
    setIsNewProductDialogOpen(true);
  };

  const handleTerpeneAdd = () => {
    if (newTerpeneName && newTerpenePercentage) {
      setNewProductPrimaryTerpenes([
        ...newProductPrimaryTerpenes,
        { name: newTerpeneName, percentage: newTerpenePercentage },
      ]);
    }
  };

  const handleTerpenePercentageChange = (index: number, percentage: number) => {
    const updatedTerpenes = [...newProductPrimaryTerpenes];
    updatedTerpenes[index] = { ...updatedTerpenes[index], percentage };
    setNewProductPrimaryTerpenes(updatedTerpenes);
  };

  const handleSaveNewProduct = async () => {
    if (!validateProduct()) {
      return;
    }

    setIsCreating(true);

    try {
      const advertisingStartDate = newProductAdvertisingStartDate?.trim() === "" 
        ? null 
        : newProductAdvertisingStartDate;
      
      const advertisingEndDate = newProductAdvertisingEndDate?.trim() === "" 
        ? null 
        : newProductAdvertisingEndDate;
        
      const availableDate = newProductAvailableDate?.trim() === "" 
        ? null 
        : newProductAvailableDate;

      const newProduct: Omit<SupabaseProduct, 'id' | 'created_at' | 'updated_at'> = {
        cultivar: newProductCultivar,
        description: newProductDescription,
        images: newProductImages,
        indica_percentage: newProductIndicaPercentage,
        parents: newProductParents,
        aroma: newProductAroma,
        feeling: newProductFeeling,
        advertising_start_date: advertisingStartDate,
        advertising_end_date: advertisingEndDate,
        rating_dank: newProductRatingDank,
        rating_earthy: newProductRatingEarthy,
        rating_pungent: newProductRatingPungent,
        rating_dessert: newProductRatingDessert,
        rating_fruity: newProductRatingFruity,
        rating_citrus: newProductRatingCitrus,
        format: newProductFormat,
        available_date: availableDate,
        grow_style: newProductGrowStyle,
        grow_media: newProductGrowMedia,
        name: newProductName
      };

      const { data, error } = await supabase
        .from('products')
        .insert([newProduct])
        .select();

      if (error) {
        console.error("Error creating product:", error);
        toast({
          title: "Error",
          description: "Failed to create product in Supabase.",
          variant: "destructive",
        });
        setIsCreating(false);
        return;
      }

      const createdProduct = mapSupabaseProduct(data[0]);
      setProducts([...products, createdProduct]);
      setFilteredProducts([...filteredProducts, createdProduct]);
      setIsNewProductDialogOpen(false);

      toast({
        title: "Success",
        description: "Product created successfully.",
      });
    } catch (error) {
      console.error("Error creating product:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred while creating the product.",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const getFilteredTerpenes = (value: string) => {
    if (value) {
      setNewTerpeneOptions(
        TERPENE_OPTIONS.filter((terpene) =>
          terpene.toLowerCase().includes(value.toLowerCase())
        )
      );
    } else {
      setNewTerpeneOptions(TERPENE_OPTIONS);
    }
  };

  const validateProduct = () => {
    if (!newProductCultivar || newProductCultivar.trim() === "") {
      toast({
        title: "Error",
        description: "Product cultivar is required.",
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const handleExportProducts = () => {
    exportProductsToCSV(products);
  };

  const handleImportProducts = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsLoading(true);
    try {
      const importedProducts = await importProductsFromCSV(file);
      
      const cultivarNames = importedProducts.map(p => p.cultivar);
      
      const existingCultivars = await checkForDuplicateCultivars(cultivarNames);
      
      if (existingCultivars.length > 0) {
        setDuplicateCultivars(existingCultivars);
        setPendingProducts(importedProducts);
        setIsDuplicateDialogOpen(true);
        setIsLoading(false);
        return;
      }
      
      await importProductsToDB(importedProducts);
    } catch (error: any) {
      toast({
        title: "Error importing products",
        description: error.message,
        variant: "destructive",
      });
      console.error("Error importing products:", error);
    } finally {
      setIsLoading(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const importProductsToDB = async (products: Product[]) => {
    let successCount = 0;
    
    for (const product of products) {
      const { error } = await supabase
        .from('products')
        .insert([{
          cultivar: product.cultivar,
          description: product.description,
          format: product.format || 'flower',
          grow_style: product.growStyle,
          grow_media: product.growMedia,
          available_date: product.availableDate === '' ? null : product.availableDate,
          indica_percentage: product.indica_percentage,
          parents: product.parents,
          aroma: product.aroma,
          feeling: product.feeling,
          rating_dank: product.rating_dank,
          rating_earthy: product.rating_earthy,
          rating_pungent: product.rating_pungent,
          rating_dessert: product.rating_dessert,
          rating_fruity: product.rating_fruity,
          rating_citrus: product.rating_citrus,
          primary_terpenes: product.terpene_info.major_terpenes,
          terpenes_data: { total: product.terpene_info.total_terpenes_percentage },
          images: []
        }]);
        
      if (!error) {
        successCount++;
      } else {
        console.error("Error importing product:", error);
      }
    }

    toast({
      title: "Success",
      description: `Imported ${successCount} products successfully.`,
    });

    fetchProducts();
  };

  const handleUpdateProductsToDB = async (products: Product[]) => {
    let successCount = 0;
    let updateCount = 0;
    
    for (const product of products) {
      const { data: existingData } = await supabase
        .from('products')
        .select('id')
        .eq('cultivar', product.cultivar)
        .limit(1);
      
      if (existingData && existingData.length > 0) {
        const { error } = await supabase
          .from('products')
          .update({
            description: product.description,
            format: product.format || 'flower',
            grow_style: product.growStyle,
            grow_media: product.growMedia,
            available_date: product.availableDate === '' ? null : product.availableDate,
            indica_percentage: product.indica_percentage,
            parents: product.parents,
            aroma: product.aroma,
            feeling: product.feeling,
            rating_dank: product.rating_dank,
            rating_earthy: product.rating_earthy,
            rating_pungent: product.rating_pungent,
            rating_dessert: product.rating_dessert,
            rating_fruity: product.rating_fruity,
            rating_citrus: product.rating_citrus,
            primary_terpenes: product.terpene_info.major_terpenes,
            terpenes_data: { total: product.terpene_info.total_terpenes_percentage },
          })
          .eq('id', existingData[0].id);
          
        if (!error) {
          updateCount++;
        } else {
          console.error("Error updating product:", error);
        }
      } else {
        const { error } = await supabase
          .from('products')
          .insert([{
            cultivar: product.cultivar,
            description: product.description,
            format: product.format || 'flower',
            grow_style: product.growStyle,
            grow_media: product.growMedia,
            available_date: product.availableDate === '' ? null : product.availableDate,
            indica_percentage: product.indica_percentage,
            parents: product.parents,
            aroma: product.aroma,
            feeling: product.feeling,
            rating_dank: product.rating_dank,
            rating_earthy: product.rating_earthy,
            rating_pungent: product.rating_pungent,
            rating_dessert: product.rating_dessert,
            rating_fruity: product.rating_fruity,
            rating_citrus: product.rating_citrus,
            primary_terpenes: product.terpene_info.major_terpenes,
            terpenes_data: { total: product.terpene_info.total_terpenes_percentage },
            images: []
          }]);
          
        if (!error) {
          successCount++;
        } else {
          console.error("Error importing product:", error);
        }
      }
    }

    toast({
      title: "Success",
      description: `Imported ${successCount} new products and updated ${updateCount} existing products.`,
    });

    fetchProducts();
  };

  const handleContinueImport = () => {
    setIsLoading(true);
    setIsDuplicateDialogOpen(false);
    
    if (importMode === 'skip') {
      const filteredProducts = pendingProducts.filter(
        product => !duplicateCultivars.includes(product.cultivar)
      );
      importProductsToDB(filteredProducts);
    } else {
      handleUpdateProductsToDB(pendingProducts);
    }
  };

  const columns: Column[] = [
    {
      key: "cultivar",
      header: "Cultivar",
      render: (_, row) => row.cultivar
    },
    {
      key: "format",
      header: "Format",
      render: (_, row) => row.format || "N/A"
    },
    {
      key: "actions",
      header: "Actions",
      render: (_, row) => (
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleProductClick(row)}
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDeleteProduct(row.id)}
          >
            <Trash className="h-4 w-4 mr-2" />
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="container mx-auto py-10">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-3xl font-bold">Products</h1>
        <div className="flex items-center space-x-2">
          <Button onClick={handleExportProducts}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <div className="relative">
            <input
              type="file"
              accept=".csv"
              onChange={handleImportProducts}
              className="hidden"
              id="importInput"
            />
            <Button
              onClick={() => document.getElementById('importInput')?.click()}
              disabled={isLoading}
            >
              <Upload className="h-4 w-4 mr-2" />
              Import
            </Button>
          </div>
          <Button onClick={handleCreateProduct}>
            <PlusCircle className="h-4 w-4 mr-2" />
            Add Product
          </Button>
        </div>
      </div>
      <div className="flex items-center space-x-2 mb-4">
        <Input
          type="text"
          placeholder="Search products..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <Button>
          <Search className="h-4 w-4" />
        </Button>
      </div>

      <CustomizableTable data={filteredProducts} columns={columns} />

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[80%] md:max-w-[70%] lg:max-w-[60%] xl:max-w-[50%] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Product</DialogTitle>
            <DialogDescription>
              Make changes to your product here. Click save when you're done.
            </DialogDescription>
          </DialogHeader>
          {selectedProduct ? (
            <div className="grid gap-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cultivar">Cultivar</Label>
                  <Input
                    type="text"
                    id="cultivar"
                    value={selectedProduct.cultivar}
                    onChange={(e) =>
                      setSelectedProduct({
                        ...selectedProduct,
                        cultivar: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={selectedProduct.description}
                  onChange={(e) =>
                    setSelectedProduct({
                      ...selectedProduct,
                      description: e.target.value,
                    })
                  }
                />
              </div>
              
              <CannabinoidPercentages
                product={selectedProduct}
                onChange={(updates) => 
                  setSelectedProduct({
                    ...selectedProduct,
                    ...updates
                  })
                }
              />

              <ProductRatings
                product={selectedProduct}
                onChange={(updates) => 
                  setSelectedProduct({
                    ...selectedProduct,
                    ...updates
                  })
                }
              />

              <div className="space-y-2">
                <Label>Terpene Percentages</Label>
                <TerpenePercentages
                  product={selectedProduct}
                  onChange={(updates) => 
                    setSelectedProduct({
                      ...selectedProduct,
                      ...updates
                    })
                  }
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="format">Format</Label>
                  <Select
                    value={selectedProduct.format || ""}
                    onValueChange={(value) =>
                      setSelectedProduct({
                        ...selectedProduct,
                        format: value as ProductFormat,
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a format" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="flower">Flower</SelectItem>
                      <SelectItem value="hash">Hash</SelectItem>
                      <SelectItem value="live rosin">Live Rosin</SelectItem>
                      <SelectItem value="resin">Resin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="available_date">Available Date</Label>
                  <Input
                    type="date"
                    id="available_date"
                    value={selectedProduct.availableDate || ""}
                    onChange={(e) =>
                      setSelectedProduct({
                        ...selectedProduct,
                        availableDate: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="grow_style">Grow Style</Label>
                  <Select
                    value={selectedProduct.growStyle || ""}
                    onValueChange={(value) =>
                      setSelectedProduct({
                        ...selectedProduct,
                        growStyle: value as GrowStyle,
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a grow style" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="indoor">Indoor</SelectItem>
                      <SelectItem value="outdoor">Outdoor</SelectItem>
                      <SelectItem value="greenhouse">Greenhouse</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="grow_media">Grow Media</Label>
                  <Select
                    value={selectedProduct.growMedia || ""}
                    onValueChange={(value) =>
                      setSelectedProduct({
                        ...selectedProduct,
                        growMedia: value as GrowMedia,
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a grow media" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="living soil">Living Soil</SelectItem>
                      <SelectItem value="coco">Coco</SelectItem>
                      <SelectItem value="rockwool">Rockwool</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="advertisingStartDate">
                    Advertising Start Date
                  </Label>
                  <Input
                    type="date"
                    id="advertisingStartDate"
                    value={selectedProduct.advertising_start_date || ""}
                    onChange={(e) =>
                      setSelectedProduct({
                        ...selectedProduct,
                        advertising_start_date: e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="advertisingEndDate">
                    Advertising End Date
                  </Label>
                  <Input
                    type="date"
                    id="advertisingEndDate"
                    value={selectedProduct.advertising_end_date || ""}
                    onChange={(e) =>
                      setSelectedProduct({
                        ...selectedProduct,
                        advertising_end_date: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="parents">Parents</Label>
                  <Input
                    type="text"
                    id="parents"
                    value={selectedProduct.parents?.join(", ") || ""}
                    onChange={(e) =>
                      setSelectedProduct({
                        ...selectedProduct,
                        parents: e.target.value.split(",").map((s) => s.trim()),
                      })
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="aroma">Aroma</Label>
                  <Input
                    type="text"
                    id="aroma"
                    value={selectedProduct.aroma || ""}
                    onChange={(e) =>
                      setSelectedProduct({
                        ...selectedProduct,
                        aroma: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="feeling">Feeling</Label>
                <Input
                  type="text"
                  id="feeling"
                  value={selectedProduct.feeling || ""}
                  onChange={(e) =>
                    setSelectedProduct({
                      ...selectedProduct,
                      feeling: e.target.value,
                    })
                  }
                />
              </div>

              <div>
                <Label>Images</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {selectedProduct.images.map((imageUrl, index) => (
                    <div key={index} className="relative">
                      <img
                        src={imageUrl}
                        alt={`Product ${index + 1}`}
                        className="w-full h-40 object-cover rounded-md"
                      />
                      <Button
                        variant="destructive"
                        size="icon"
                        className="absolute top-2 right-2"
                        onClick={() => handleRemoveImage(imageUrl)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label>Add New Image</Label>
                <div className="flex items-center space-x-2">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    ref={inputRef}
                  />
                  <Button
                    onClick={handleAddImage}
                    disabled={uploading || !selectedImage}
                  >
                    {uploading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ImagePlus className="h-4 w-4 mr-2" />
                    )}
                    {uploading ? "Uploading..." : "Add Image"}
                  </Button>
                </div>
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button onClick={handleSaveProduct} disabled={isSaving}>
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  {isSaving ? "Saving..." : "Save changes"}
                </Button>
              </DialogFooter>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={isNewProductDialogOpen} onOpenChange={setIsNewProductDialogOpen}>
        <DialogContent className="sm:max-w-[80%] md:max-w-[70%] lg:max-w-[60%] xl:max-w-[50%] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Product</DialogTitle>
            <DialogDescription>
              Fill in the details for your new product. Click create when you're done.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Product Name</Label>
                <Input
                  type="text"
                  id="name"
                  value={newProductName}
                  onChange={(e) => setNewProductName(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="cultivar">Cultivar</Label>
                <Input
                  type="text"
                  id="cultivar"
                  value={newProductCultivar}
                  onChange={(e) => setNewProductCultivar(e.target.value)}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={newProductDescription}
                onChange={(e) => setNewProductDescription(e.target.value)}
              />
            </div>
            
            {/* Cannabinoid fields have been moved to batches - create a batch to set cannabinoid data */}
            <div className="text-sm text-muted-foreground mb-4">
              Cannabinoid information is now managed through individual batches. Create batches to specify cannabinoid profiles.
            </div>

            <ProductRatings
              product={{
                rating_dank: newProductRatingDank || 0,
                rating_earthy: newProductRatingEarthy || 0,
                rating_pungent: newProductRatingPungent || 0,
                rating_dessert: newProductRatingDessert || 0,
                rating_fruity: newProductRatingFruity || 0,
                rating_citrus: newProductRatingCitrus || 0
              }}
              onChange={(updates) => {
                setNewProductRatingDank(updates.rating_dank);
                setNewProductRatingEarthy(updates.rating_earthy);
                setNewProductRatingPungent(updates.rating_pungent);
                setNewProductRatingDessert(updates.rating_dessert);
                setNewProductRatingFruity(updates.rating_fruity);
                setNewProductRatingCitrus(updates.rating_citrus);
              }}
            />

            <div className="space-y-2">
              <Label>Terpene Percentages</Label>
              <TerpenePercentages
                product={{
                  terpene_info: {
                    major_terpenes: newProductPrimaryTerpenes,
                    total_terpenes_percentage: 0
                  }
                }}
                onChange={(updates) => {
                  if (updates.terpene_info) {
                    setNewProductPrimaryTerpenes(updates.terpene_info.major_terpenes);
                  }
                }}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="format">Format</Label>
                <Select
                  value={newProductFormat || ""}
                  onValueChange={(value) => setNewProductFormat(value as ProductFormat)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a format" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="flower">Flower</SelectItem>
                    <SelectItem value="hash">Hash</SelectItem>
                    <SelectItem value="live rosin">Live Rosin</SelectItem>
                    <SelectItem value="resin">Resin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="available_date">Available Date</Label>
                <Input
                  type="date"
                  id="available_date"
                  value={newProductAvailableDateString}
                  onChange={(e) => {
                    setNewProductAvailableDateString(e.target.value);
                    setNewProductAvailableDate(e.target.value);
                  }}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="grow_style">Grow Style</Label>
                <Select
                  value={newProductGrowStyle || ""}
                  onValueChange={(value) => setNewProductGrowStyle(value as GrowStyle)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a grow style" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="indoor">Indoor</SelectItem>
                    <SelectItem value="outdoor">Outdoor</SelectItem>
                    <SelectItem value="greenhouse">Greenhouse</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="grow_media">Grow Media</Label>
                <Select
                  value={newProductGrowMedia || ""}
                  onValueChange={(value) => setNewProductGrowMedia(value as GrowMedia)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a grow media" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="living soil">Living Soil</SelectItem>
                    <SelectItem value="coco">Coco</SelectItem>
                    <SelectItem value="rockwool">Rockwool</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="advertisingStartDate">Advertising Start Date</Label>
                <Input
                  type="date"
                  id="advertisingStartDate"
                  value={newProductAdvertisingStartDateString}
                  onChange={(e) => {
                    setNewProductAdvertisingStartDateString(e.target.value);
                    setNewProductAdvertisingStartDate(e.target.value);
                  }}
                />
              </div>
              <div>
                <Label htmlFor="advertisingEndDate">Advertising End Date</Label>
                <Input
                  type="date"
                  id="advertisingEndDate"
                  value={newProductAdvertisingEndDateString}
                  onChange={(e) => {
                    setNewProductAdvertisingEndDateString(e.target.value);
                    setNewProductAdvertisingEndDate(e.target.value);
                  }}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="parents">Parents</Label>
                <Input
                  type="text"
                  id="parents"
                  value={newProductParents?.join(", ") || ""}
                  onChange={(e) =>
                    setNewProductParents(e.target.value.split(",").map((s) => s.trim()))
                  }
                />
              </div>
              <div>
                <Label htmlFor="aroma">Aroma</Label>
                <Input
                  type="text"
                  id="aroma"
                  value={newProductAroma}
                  onChange={(e) => setNewProductAroma(e.target.value)}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="feeling">Feeling</Label>
              <Input
                type="text"
                id="feeling"
                value={newProductFeeling}
                onChange={(e) => setNewProductFeeling(e.target.value)}
              />
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsNewProductDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button onClick={handleSaveNewProduct} disabled={isCreating}>
                {isCreating ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                {isCreating ? "Creating..." : "Create Product"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isDuplicateDialogOpen} onOpenChange={setIsDuplicateDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Duplicate Products Detected</DialogTitle>
            <DialogDescription>
              {duplicateCultivars.length} products with the same cultivar names already exist. 
              How would you like to proceed?
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 my-4">
            <div className="max-h-[200px] overflow-y-auto border rounded-md p-2">
              <p className="font-medium mb-2">Duplicate cultivars:</p>
              <ul className="list-disc list-inside space-y-1">
                {duplicateCultivars.map((cultivar, index) => (
                  <li key={index} className="text-sm">{cultivar}</li>
                ))}
              </ul>
            </div>
            
            <div className="space-y-2">
              <Label>Import Mode</Label>
              <Select value={importMode} onValueChange={(value: 'skip' | 'update') => setImportMode(value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select import mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="skip">Skip duplicates (import only new products)</SelectItem>
                  <SelectItem value="update">Update duplicates (overwrite existing products)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDuplicateDialogOpen(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button onClick={handleContinueImport} disabled={isLoading}>
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Continue Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsAdmin;
